import { z } from "zod";

/**
 * Account step schema — owner identity + login credentials.
 *
 * Two emails are collected:
 *   - email          → Bill_User identity (invoize.me login + invoice receipt)
 *   - project_email  → project-specific user identity (login the actual
 *                       SaaS app, e.g. SmartLaw, Rentaru, LeaveHub)
 *
 * They MAY be the same value (most common case for solo owners), but
 * the form keeps them as separate fields so users with team workflows
 * can route product login and billing to different inboxes. Each is
 * validated against its own uniqueness rule:
 *   - Bill_User.email must be globally unique in pkindev_platform
 *   - <project>.user.email must be unique within that project schema
 *
 * Password rules:
 *   - min 8 chars
 *   - at least one uppercase letter
 *   - at least one digit
 *   - confirm must match
 *
 * Note: not enforcing special-character requirement to keep signup friction
 * low. NIST guidance (SP 800-63B) prefers length over composition rules.
 */

export const accountSchema = z
  .object({
    first_name: z
      .string()
      .trim()
      .min(1, "กรุณากรอกชื่อ")
      .max(50, "ชื่อยาวเกินไป"),

    last_name: z
      .string()
      .trim()
      .min(1, "กรุณากรอกนามสกุล")
      .max(50, "นามสกุลยาวเกินไป"),

    // Bill_User email — used to log into invoize.me and to receive
    // invoice / receipt emails. Globally unique in pkindev_platform.
    email: z
      .string()
      .trim()
      .min(1, "กรุณากรอกอีเมล")
      .email("รูปแบบอีเมลไม่ถูกต้อง")
      .max(100, "อีเมลยาวเกินไป"),

    // Project user email — used to log into the SaaS app being signed
    // up for. Unique within the project schema. Often the same value
    // as `email` above; we let the user pick.
    project_email: z
      .string()
      .trim()
      .min(1, "กรุณากรอกอีเมลสำหรับเข้าใช้งานระบบ")
      .email("รูปแบบอีเมลไม่ถูกต้อง")
      .max(100, "อีเมลยาวเกินไป"),

    password: z
      .string()
      .min(8, "รหัสผ่านต้องยาวอย่างน้อย 8 ตัวอักษร")
      .max(72, "รหัสผ่านยาวเกินไป (สูงสุด 72 ตัวอักษร)") // bcrypt limit
      .regex(/[A-Z]/, "ต้องมีตัวพิมพ์ใหญ่อย่างน้อย 1 ตัว")
      .regex(/[0-9]/, "ต้องมีตัวเลขอย่างน้อย 1 ตัว"),

    confirm_password: z.string().min(1, "กรุณายืนยันรหัสผ่าน"),

    // Plan can change at this step via the carousel; the form holds the
    // currently selected ID. Required because user must pick something.
    id_subscription_plan_table: z
      .union([z.string(), z.number()])
      .refine((v) => Number(v) > 0, "กรุณาเลือกแพ็กเกจ"),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: "รหัสผ่านไม่ตรงกัน",
    path: ["confirm_password"],
  });